/**
 * Offline scan queue of the Operator_Console (Req 13.1, 13.2, 28.4; design "Clients →
 * Operator_Console", "Client offline", Property 36).
 *
 * Enqueue: each scan gets a client UUIDv7 Idempotency_Key and the device's `scannedAt`,
 * plus the tenant, outlet and operator selected at scan time. The scope is frozen on the
 * item, so switching outlet before the flush cannot misattribute the claim. Items are
 * persisted (IndexedDB `scans`) before `enqueue` resolves.
 *
 * Flush (on reconnect, Background Sync relay, sign-in, retry timer, or a manual call):
 *  - items are submitted one at a time in key (= scan) order, always with their original
 *    key, `scannedAt`, tenant and outlet, so a resubmission returns the original result
 *    (Req 13.4) and expiry is judged on the real scan time (`scan_expired`, Req 13.5);
 *  - an item leaves the pending set only after a definitive server response:
 *      2xx                          → `done`, ClaimResult stored;
 *      4xx (not 401/408/429)        → `rejected`, problem stored for the operator to review
 *                                     (`already_claimed`, `code_inactive`, `invalid_code`,
 *                                     `scan_expired`, `idempotency_conflict`, `forbidden`…);
 *      429 (`velocity_limit`,       → `retrying` with `notBefore` from Retry-After; the run
 *           `claims_locked`)          continues with other items (limits are per member);
 *      401                          → back to `queued`, run stops (the session renews or
 *                                     the operator signs in again; nothing is lost);
 *      network error / 408 / 5xx    → `retrying`, run stops, retried with backoff;
 *  - only scans of the signed-in operator are submitted: the server scopes idempotency
 *    keys per staff user, and a claim must not be attributed to a different operator;
 *  - single-flight: concurrent `flush()` calls share one run (a call during a run triggers
 *    one follow-up pass for newly queued items). Across tabs the run holds a Web Lock
 *    (`navigator.locks`, injected as `lock`), and items are re-read inside the lock, so two
 *    tabs never submit the same item at once. Without Web Locks two tabs may race; the
 *    shared key makes the duplicate submission harmless (the server answers the original
 *    result or `idempotency_conflict` for a concurrent in-flight duplicate, never a second
 *    claim). An item left `submitting` by a closed tab is resubmitted with the same key.
 *
 * Items older than 30 days are not dropped locally: the server answers `scan_expired` and
 * the item is kept as `rejected` so the operator sees it. `done` items are pruned after
 * `keepDoneMs` (default 7 days); `rejected` items stay until dismissed.
 *
 * Security note: queued short codes are bearer secrets until claimed and stay in
 * IndexedDB on the device, including after sign-out (deleting them would lose scans).
 */
import type { components } from '@macksons/contracts';
import { uuidv7 as defaultKey } from './uuidv7';
import type { ScanStore } from './scan-store';

export type ClaimResult = components['schemas']['ClaimResult'];

export type ScanStatus = 'queued' | 'submitting' | 'retrying' | 'done' | 'rejected';
export const PENDING_STATUSES: readonly ScanStatus[] = ['queued', 'submitting', 'retrying'];

/** Tenant, outlet and operator in effect when the scan was taken. */
export interface ScanScope {
  tenantId: string;
  outletId: string;
  /** Staff `sub`. */
  operatorUserId: string;
}

/** Problem returned by the server (or `network_error`, status 0). */
export interface ScanProblem {
  status: number;
  code: string;
  title?: string;
  detail?: string;
  traceId?: string;
}

export interface QueuedScan extends ScanScope {
  /** Client-generated UUIDv7 Idempotency_Key, sent with every submission of this scan. */
  idempotencyKey: string;
  shortCode: string;
  /** ISO timestamp of the scan on the device. */
  scannedAt: string;
  memberId: string;
  status: ScanStatus;
  /** Submissions so far. */
  attempts: number;
  lastAttemptAt?: string;
  /** Earliest next submission (from Retry-After), ISO. */
  notBefore?: string;
  /** Server result once `done`. */
  result?: ClaimResult;
  /** Last problem: final when `rejected`, informational while `retrying`. */
  problem?: ScanProblem;
  completedAt?: string;
}

export type SubmitOutcome =
  | { ok: true; result: ClaimResult }
  | { ok: false; problem: ScanProblem; retryAfterMs?: number };

/** Submits one scan (POST /v1/ops/claims). Must not throw for HTTP or network errors. */
export type ClaimTransport = (scan: QueuedScan) => Promise<SubmitOutcome>;

export type FlushStop = 'offline' | 'signed_out' | 'auth' | 'unavailable';

export interface FlushReport {
  submitted: number;
  done: number;
  rejected: number;
  /** Still pending after this run (deferred by Retry-After, failed, or not reached). */
  pending: number;
  stopped?: FlushStop;
}

export interface ScanQueueSnapshot {
  loaded: boolean;
  /** All items in scan order, including `done` and `rejected` ones kept for review. */
  items: readonly QueuedScan[];
  pendingCount: number;
  flushing: boolean;
  lastFlush?: FlushReport;
}

export interface ScanQueue {
  enqueue(scan: Pick<QueuedScan, 'shortCode' | 'memberId'>): Promise<QueuedScan>;
  /** Items not yet answered definitively by the server, in scan order. */
  pending(): Promise<QueuedScan[]>;
  /** Submits queued scans; items leave the pending set only after a server response. */
  flush(): Promise<FlushReport>;
  /** Removes a reviewed `done` or `rejected` item. Pending items cannot be dismissed. */
  dismiss(idempotencyKey: string): Promise<void>;
  getSnapshot(): ScanQueueSnapshot;
  subscribe(listener: () => void): () => void;
  /** Reloads the snapshot from the store (e.g. after another tab changed it). */
  refresh(): Promise<void>;
  dispose(): void;
}

export interface QueueClock {
  now(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const systemClock: QueueClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

/** Minimal BroadcastChannel surface used to tell other tabs the store changed. */
export interface ChangeChannel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: () => void): void;
  removeEventListener(type: 'message', listener: () => void): void;
  close?(): void;
}

export interface ScanQueueDeps {
  store: ScanStore;
  transport: ClaimTransport;
  /** Scope for new scans; `undefined` when not signed in or no outlet is selected. */
  getScope: () => ScanScope | undefined;
  /** Signed-in staff `sub`; only that operator's scans are submitted. */
  getOperatorId: () => string | undefined;
  /** False while the console is offline: flush returns without touching items. */
  canSubmit?: () => boolean;
  clock?: QueueClock;
  newKey?: (nowMs: number) => string;
  /** Exclusive cross-tab section (Web Locks). Default: in-process only. */
  lock?: <T>(fn: () => Promise<T>) => Promise<T>;
  channel?: ChangeChannel;
  /** Called after a scan is persisted (registers Background Sync). */
  onEnqueued?: (scan: QueuedScan) => void;
  backoff?: { baseMs: number; maxMs: number };
  /** Delay for a 429 without Retry-After. */
  defaultRetryAfterMs?: number;
  keepDoneMs?: number;
}

export const isPending = (s: Pick<QueuedScan, 'status'>): boolean => PENDING_STATUSES.includes(s.status);
const byKey = (a: QueuedScan, b: QueuedScan) => (a.idempotencyKey < b.idempotencyKey ? -1 : a.idempotencyKey > b.idempotencyKey ? 1 : 0);

const DAY_MS = 24 * 3600 * 1000;

function omit<T extends object, K extends keyof T>(value: T, key: K): Omit<T, K> {
  const copy = { ...value };
  delete copy[key];
  return copy;
}

/** How a failed submission affects the item and the run. */
function classify(problem: ScanProblem): 'auth' | 'defer' | 'transient' | 'final' {
  if (problem.status === 401) return 'auth';
  if (problem.status === 429) return 'defer';
  if (problem.status === 0 || problem.status === 408 || problem.status >= 500) return 'transient';
  return 'final';
}

export function createScanQueue(deps: ScanQueueDeps): ScanQueue {
  const clock = deps.clock ?? systemClock;
  const newKey = deps.newKey ?? defaultKey;
  const lock = deps.lock ?? ((fn) => fn());
  const backoff = deps.backoff ?? { baseMs: 5_000, maxMs: 5 * 60_000 };
  const defaultRetryAfterMs = deps.defaultRetryAfterMs ?? 60_000;
  const keepDoneMs = deps.keepDoneMs ?? 7 * DAY_MS;
  const iso = (ms: number) => new Date(ms).toISOString();

  let snapshot: ScanQueueSnapshot = { loaded: false, items: [], pendingCount: 0, flushing: false };
  const listeners = new Set<() => void>();
  const setSnapshot = (patch: Partial<ScanQueueSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    for (const l of listeners) l();
  };

  async function refresh(): Promise<void> {
    const items = (await deps.store.all()).sort(byKey);
    setSnapshot({ loaded: true, items, pendingCount: items.filter(isPending).length });
  }

  /** Persists one item, then updates this tab's snapshot and tells other tabs. */
  async function save(scan: QueuedScan): Promise<void> {
    await deps.store.put(scan);
    deps.channel?.postMessage({ type: 'changed' });
    await refresh();
  }

  const onChannelMessage = () => void refresh();
  deps.channel?.addEventListener('message', onChannelMessage);

  let consecutiveFailures = 0;
  let timer: unknown;
  const schedule = (atMs: number | undefined) => {
    if (timer !== undefined) clock.clearTimeout(timer);
    timer = undefined;
    if (atMs === undefined) return;
    timer = clock.setTimeout(() => {
      timer = undefined;
      void flush();
    }, Math.max(0, atMs - clock.now()));
  };

  async function run(): Promise<FlushReport> {
    const report: FlushReport = { submitted: 0, done: 0, rejected: 0, pending: 0 };
    let retryAt: number | undefined;
    const operatorId = deps.getOperatorId();
    if (deps.canSubmit && !deps.canSubmit()) report.stopped = 'offline';
    else if (!operatorId) report.stopped = 'signed_out';

    // Read inside the lock: another tab may have finished items meanwhile.
    const all = (await deps.store.all()).sort(byKey);
    const now = clock.now();
    let pruned = false;
    for (const old of all) {
      if (old.status === 'done' && old.completedAt && Date.parse(old.completedAt) + keepDoneMs <= now) {
        await deps.store.delete(old.idempotencyKey);
        pruned = true;
      }
    }
    if (report.stopped) {
      // The snapshot must not keep showing pruned items when the run stops early.
      if (pruned) await refresh();
      report.pending = all.filter(isPending).length;
      return report;
    }

    const mine = all.filter((s) => isPending(s) && s.operatorUserId === operatorId);
    for (const item of mine) {
      const t = clock.now();
      if (item.notBefore && Date.parse(item.notBefore) > t) {
        retryAt = Math.min(retryAt ?? Infinity, Date.parse(item.notBefore));
        continue;
      }
      const submitting: QueuedScan = { ...item, status: 'submitting', attempts: item.attempts + 1, lastAttemptAt: iso(t) };
      await save(submitting);
      report.submitted++;
      let outcome: SubmitOutcome;
      try {
        outcome = await deps.transport(submitting);
      } catch (cause) {
        outcome = { ok: false, problem: { status: 0, code: 'network_error', ...(cause instanceof Error ? { detail: cause.message } : {}) } };
      }
      const after = clock.now();
      const base = omit(submitting, 'notBefore');
      if (outcome.ok) {
        await save({ ...omit(base, 'problem'), status: 'done', result: outcome.result, completedAt: iso(after) });
        report.done++;
        consecutiveFailures = 0;
        continue;
      }
      const kind = classify(outcome.problem);
      if (kind === 'final') {
        await save({ ...base, status: 'rejected', problem: outcome.problem, completedAt: iso(after) });
        report.rejected++;
        consecutiveFailures = 0;
        continue;
      }
      if (kind === 'defer') {
        const at = after + (outcome.retryAfterMs ?? defaultRetryAfterMs);
        await save({ ...base, status: 'retrying', problem: outcome.problem, notBefore: iso(at) });
        retryAt = Math.min(retryAt ?? Infinity, at);
        continue;
      }
      if (kind === 'auth') {
        // Not the scan's fault: keep it queued without counting the attempt.
        await save({ ...base, status: 'queued', attempts: item.attempts, problem: outcome.problem });
        report.stopped = 'auth';
        break;
      }
      consecutiveFailures++;
      const delay = Math.min(backoff.baseMs * 2 ** (consecutiveFailures - 1), backoff.maxMs);
      await save({ ...base, status: 'retrying', problem: outcome.problem });
      retryAt = Math.min(retryAt ?? Infinity, after + Math.max(delay, outcome.retryAfterMs ?? 0));
      report.stopped = 'unavailable';
      break;
    }
    // An auth stop waits for the next sign-in instead of a timer.
    schedule(report.stopped === 'auth' ? undefined : retryAt);
    await refresh();
    report.pending = snapshot.pendingCount;
    return report;
  }

  let running: Promise<FlushReport> | undefined;
  let again = false;
  function flush(): Promise<FlushReport> {
    if (running) {
      again = true;
      return running;
    }
    setSnapshot({ flushing: true });
    running = (async () => {
      let report: FlushReport;
      do {
        again = false;
        report = await lock(run);
      } while (again && !report.stopped);
      return report;
    })()
      .then((report) => {
        setSnapshot({ lastFlush: report });
        return report;
      })
      .finally(() => {
        running = undefined;
        setSnapshot({ flushing: false });
      });
    return running;
  }

  return {
    async enqueue({ shortCode, memberId }) {
      const scope = deps.getScope();
      if (!scope) throw new Error('scan queue: no tenant, outlet or operator selected');
      const now = clock.now();
      const scan: QueuedScan = {
        idempotencyKey: newKey(now),
        shortCode,
        scannedAt: iso(now),
        memberId,
        tenantId: scope.tenantId,
        outletId: scope.outletId,
        operatorUserId: scope.operatorUserId,
        status: 'queued',
        attempts: 0,
      };
      await save(scan);
      deps.onEnqueued?.(scan);
      return scan;
    },
    async pending() {
      return (await deps.store.all()).filter(isPending).sort(byKey);
    },
    flush,
    async dismiss(key) {
      const item = await deps.store.get(key);
      if (!item || isPending(item)) return;
      await deps.store.delete(key);
      deps.channel?.postMessage({ type: 'changed' });
      await refresh();
    },
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      if (!snapshot.loaded) void refresh();
      return () => listeners.delete(listener);
    },
    refresh,
    dispose() {
      schedule(undefined);
      deps.channel?.removeEventListener('message', onChannelMessage);
      deps.channel?.close?.();
    },
  };
}

/** Exclusive Web Lock across tabs of the origin, when the browser supports it. */
export function webLock(name = 'macksons-scan-queue', locks: LockManager | undefined = globalThis.navigator?.locks) {
  if (!locks) return undefined;
  return <T>(fn: () => Promise<T>): Promise<T> => locks.request(name, { mode: 'exclusive' }, fn) as Promise<T>;
}
